import React, { useState } from 'react';
import { Truck, PlusCircle, X, Package, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { API } from '@/utils/api';
import { toast } from 'sonner';
import axios from 'axios';

const STATUS_CONFIG = {
  pas_commande: { label: 'Pas commande', color: 'bg-red-100 text-red-700 border-red-300', icon: AlertCircle, dotColor: 'bg-red-500' },
  en_cours: { label: 'Commande en cours', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Clock, dotColor: 'bg-amber-500' },
  recu: { label: 'Recu', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle, dotColor: 'bg-emerald-500' },
};

const SUPPLIERS = [
  { value: 'Jacquet', label: 'Jacquet', borderColor: 'border-blue-300', bgColor: 'bg-blue-50', textColor: 'text-blue-700', logo: 'https://customer-assets.emergentagent.com/job_0a9ef812-a1a0-475e-80b5-a2cc4054894a/artifacts/2dg7fc4q_image.png' },
  { value: 'ESA', label: 'ESA', borderColor: 'border-red-300', bgColor: 'bg-red-50', textColor: 'text-red-700', logo: 'https://www.esa.ch/assets/esa/img/logo/logo_de.jpg' },
  { value: 'EC24', label: 'EC24', borderColor: 'border-amber-300', bgColor: 'bg-amber-50', textColor: 'text-amber-700', logo: 'https://tm5.carparts-cat.com/styles/lkq/images/favicon_ec24.png' },
];

export const SuppliersOrderSection = ({ woId, partsOrders, setPartsOrders }) => {
  const [customSupplier, setCustomSupplier] = useState('');

  const savePartsOrders = async (orders) => {
    try {
      await axios.put(`${API}/work-orders/${woId}`, { parts_orders: orders });
    } catch { /* auto-save */ }
  };

  const addPartsOrder = (supplier = '') => {
    const now = new Date();
    const localIso = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}T${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    const newOrders = [...(partsOrders || []), {
      supplier: supplier, description: '', status: 'pas_commande', ordered_at: localIso,
      delivery_date: '', delivery_time: 'matin',
    }];
    setPartsOrders(newOrders);
    savePartsOrders(newOrders);
  };

  const removePartsOrder = (idx) => {
    const newOrders = (partsOrders || []).filter((_, i) => i !== idx);
    setPartsOrders(newOrders);
    savePartsOrders(newOrders);
  };

  const updatePartsOrder = (idx, field, value) => {
    const newOrders = (partsOrders || []).map((o, i) => i === idx ? { ...o, [field]: value } : o);
    setPartsOrders(newOrders);
    savePartsOrders(newOrders);

    // Check if all parts are now received
    if (field === 'status' && value === 'recu') {
      const allReceived = newOrders.every(o => o.status === 'recu');
      if (allReceived && newOrders.length > 0) {
        toast.success('Toutes les pieces sont recues ! La fiche est prete.');
      }
    }
  };

  const addCustomSupplier = () => {
    if (!customSupplier.trim()) return;
    addPartsOrder(customSupplier.trim());
    setCustomSupplier('');
  };

  const orders = partsOrders || [];
  const totalOrders = orders.length;
  const receivedCount = orders.filter(o => o.status === 'recu').length;
  const pendingCount = orders.filter(o => o.status === 'en_cours').length;
  const notOrderedCount = orders.filter(o => o.status === 'pas_commande').length;

  return (
    <Card className="p-4 rounded-xl space-y-3" data-testid="wo-suppliers-section">
      {/* Header with summary badges */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <Truck className="w-4 h-4" /> Pieces a commander
        </h3>
        {totalOrders > 0 && (
          <div className="flex items-center gap-1.5" data-testid="parts-order-summary">
            {notOrderedCount > 0 && <Badge className="bg-red-100 text-red-700 text-[10px] border border-red-200">{notOrderedCount} pas commande</Badge>}
            {pendingCount > 0 && <Badge className="bg-amber-100 text-amber-700 text-[10px] border border-amber-200">{pendingCount} en cours</Badge>}
            {receivedCount > 0 && <Badge className="bg-emerald-100 text-emerald-700 text-[10px] border border-emerald-200">{receivedCount} recu</Badge>}
          </div>
        )}
      </div>

      {/* Quick supplier buttons */}
      <div className="flex gap-2 flex-wrap">
        {SUPPLIERS.map(s => (
          <Button
            key={s.value}
            variant="outline"
            size="sm"
            className={`h-10 text-xs font-bold border-2 ${s.borderColor} ${s.bgColor} hover:opacity-80 transition-all gap-2`}
            onClick={() => addPartsOrder(s.value)}
            data-testid={`supplier-btn-${s.value.toLowerCase()}`}
          >
            <img src={s.logo} alt={s.label} className="h-5 object-contain" onError={(e) => { e.target.style.display='none'; }} />
            <span className={s.textColor}>{s.label}</span>
          </Button>
        ))}
        {/* Custom supplier input */}
        <div className="flex items-center gap-1">
          <Input
            value={customSupplier}
            onChange={(e) => setCustomSupplier(e.target.value)}
            placeholder="Autre fournisseur..."
            className="h-10 text-xs w-36"
            onKeyDown={(e) => e.key === 'Enter' && addCustomSupplier()}
            data-testid="custom-supplier-input"
          />
          <Button variant="outline" size="sm" className="h-10" onClick={addCustomSupplier} disabled={!customSupplier.trim()} data-testid="add-custom-supplier-btn">
            <PlusCircle className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Supplier links */}
      <div className="flex gap-2 text-[10px] text-slate-400">
        <a href="https://www.esa.ch" target="_blank" rel="noopener noreferrer" className="hover:text-red-600 underline">esa.ch</a>
        <a href="https://tm2.carparts-cat.com/login/jaquet" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 underline">Jacquet portail</a>
        <a href="https://tm5.carparts-cat.com/login/ec24" target="_blank" rel="noopener noreferrer" className="hover:text-amber-600 underline">EC24 portail</a>
      </div>

      {/* Parts orders list */}
      <div className="border-t border-slate-100 pt-3 space-y-3">
        {orders.length === 0 ? (
          <div className="text-center py-4 border-2 border-dashed border-slate-200 rounded-lg">
            <Package className="w-6 h-6 mx-auto text-slate-300 mb-1" />
            <p className="text-sm text-slate-400">Aucune piece a commander</p>
            <p className="text-xs text-slate-300 mt-0.5">Cliquez sur un fournisseur pour ajouter une commande</p>
          </div>
        ) : (
          <div className="space-y-2">
            {orders.map((order, idx) => {
              const statusConf = STATUS_CONFIG[order.status] || STATUS_CONFIG.pas_commande;
              const StatusIcon = statusConf.icon;
              const supplierInfo = SUPPLIERS.find(s => s.value === order.supplier);

              return (
                <div
                  key={idx}
                  className={`p-3 rounded-xl border-2 transition-colors ${
                    order.status === 'recu' ? 'border-emerald-200 bg-emerald-50/30' :
                    order.status === 'en_cours' ? 'border-amber-200 bg-amber-50/30' :
                    'border-red-200 bg-red-50/30'
                  }`}
                  data-testid={`parts-order-${idx}`}
                >
                  {/* Top row: supplier + status + delete */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-full ${statusConf.dotColor}`} />
                      <span className="text-sm font-bold text-slate-800">
                        {order.supplier || 'Fournisseur ?'}
                      </span>
                      {supplierInfo && (
                        <img src={supplierInfo.logo} alt="" className="h-4 object-contain" onError={(e) => { e.target.style.display='none'; }} />
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Badge className={`text-xs border ${statusConf.color} flex items-center gap-1`} data-testid={`order-status-badge-${idx}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConf.label}
                      </Badge>
                      <Button variant="ghost" size="sm" onClick={() => removePartsOrder(idx)} className="text-red-400 hover:text-red-600 h-6 w-6 p-0" data-testid={`remove-order-${idx}`}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* Status buttons - prominent */}
                  <div className="flex items-center gap-1.5 mb-2" data-testid={`status-buttons-${idx}`}>
                    {Object.entries(STATUS_CONFIG).map(([key, conf]) => {
                      const Icon = conf.icon;
                      const isActive = order.status === key;
                      return (
                        <button
                          key={key}
                          onClick={() => updatePartsOrder(idx, 'status', key)}
                          className={`flex-1 py-1.5 px-2 rounded-lg text-xs font-bold border-2 transition-all flex items-center justify-center gap-1 ${
                            isActive ? `${conf.color} border-current shadow-sm` : 'border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-600'
                          }`}
                          data-testid={`status-btn-${key}-${idx}`}
                        >
                          <Icon className="w-3 h-3" />
                          {conf.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Details row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div>
                      <label className="text-[10px] text-slate-500">Fournisseur</label>
                      <Select value={order.supplier || ''} onValueChange={(v) => updatePartsOrder(idx, 'supplier', v)}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`order-supplier-${idx}`}><SelectValue placeholder="Choisir..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Jacquet">Jacquet</SelectItem>
                          <SelectItem value="ESA">ESA</SelectItem>
                          <SelectItem value="EC24">EC24</SelectItem>
                          <SelectItem value="autre">Autre...</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500">Description</label>
                      <Input value={order.description || ''} onChange={(e) => updatePartsOrder(idx, 'description', e.target.value)} placeholder="Pieces..." className="h-8 text-xs" data-testid={`order-desc-${idx}`} />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500">Date livraison</label>
                      <Input type="date" value={order.delivery_date || ''} onChange={(e) => updatePartsOrder(idx, 'delivery_date', e.target.value)} className="h-8 text-xs" data-testid={`delivery-date-${idx}`} />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500">Livraison</label>
                      <Select value={order.delivery_time || 'matin'} onValueChange={(v) => updatePartsOrder(idx, 'delivery_time', v)}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`delivery-time-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="matin">Matin</SelectItem>
                          <SelectItem value="apres-midi">Apres-midi</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
};
